/**
 * MegaMenu — the competitor-class two-column nav dropdown for the three
 * modality entries (Image / Video / Audio):
 *
 *   Features | everything the platform ACTUALLY ships for that modality
 *              (icon tile + name + one-line description, real routes)
 *   Models   | the live registry, richest-first: vendor mark tile, name,
 *              badge, tagline — clicking selects the model via
 *              /create/:modality?model=<id>; footer deep-links the full
 *              picker (?picker=1).
 *
 * Fully data-driven: features are config, models come from the registry
 * query — a newly published manifest appears here with zero code changes.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Brush, ImageUpscale, UserRound } from 'lucide-react';
import type { PublicModel } from '@vidply/shared';
import { isToolModel } from '@vidply/shared';
import { api } from '../lib/api';
import {
  ArrowRight,
  AudioLines,
  Bot,
  Clapperboard,
  Film,
  ImageIcon,
  Layers,
  LayoutTemplate,
  Mic,
  Sparkles,
  Wand2,
} from './icons';
import { VendorMark } from './VendorMark';
import { Badge } from './ui';

type MenuModality = 'image' | 'video' | 'audio';

interface FeatureEntry {
  icon: typeof ImageIcon;
  labelKey: string;
  descKey: string;
  href: string;
  soon?: boolean;
}

/** What we actually ship, per modality — honest routes only. */
const FEATURES: Record<MenuModality, FeatureEntry[]> = {
  image: [
    { icon: ImageIcon, labelKey: 'home.toolT2I', descKey: 'home.toolT2IDesc', href: '/create/image' },
    { icon: LayoutTemplate, labelKey: 'nav.templates', descKey: 'mega.templatesDesc', href: '/templates' },
    { icon: Brush, labelKey: 'mega.inpaintTitle', descKey: 'mega.inpaintDesc', href: '/tools/inpaint' },
    { icon: UserRound, labelKey: 'mega.swapTitle', descKey: 'mega.swapDesc', href: '/tools/character-swap' },
    { icon: ImageUpscale, labelKey: 'mega.upscaleTitle', descKey: 'mega.upscaleDesc', href: '/tools/upscale' },
    { icon: Sparkles, labelKey: 'characters.title', descKey: 'characters.sub', href: '/characters' },
    { icon: Wand2, labelKey: 'studio.title', descKey: 'mega.marketingDesc', href: '/studio/marketing' },
    { icon: Layers, labelKey: 'nav.canvas', descKey: 'mega.canvasDesc', href: '/soon/canvas', soon: true },
  ],
  video: [
    { icon: Clapperboard, labelKey: 'home.toolT2V', descKey: 'home.toolT2VDesc', href: '/create/video' },
    { icon: LayoutTemplate, labelKey: 'nav.templates', descKey: 'mega.templatesDesc', href: '/templates' },
    { icon: Wand2, labelKey: 'mega.editVideoTitle', descKey: 'mega.editVideoDesc', href: '/tools/edit-video' },
    { icon: Mic, labelKey: 'mega.lipsyncTitle', descKey: 'mega.lipsyncDesc', href: '/tools/lipsync' },
    { icon: ImageUpscale, labelKey: 'mega.upscaleTitle', descKey: 'mega.upscaleDesc', href: '/tools/upscale' },
    { icon: Film, labelKey: 'cinema.title', descKey: 'cinema.sub', href: '/studio/cinema' },
    { icon: Wand2, labelKey: 'studio.title', descKey: 'mega.marketingDesc', href: '/studio/marketing' },
    { icon: AudioLines, labelKey: 'faceless.title', descKey: 'faceless.sub', href: '/studio/faceless' },
    { icon: Bot, labelKey: 'nav.supercomputer', descKey: 'mega.superDesc', href: '/supercomputer' },
  ],
  audio: [
    { icon: Mic, labelKey: 'home.toolAudio', descKey: 'home.toolAudioDesc', href: '/create/audio' },
    { icon: AudioLines, labelKey: 'mega.lipsyncTitle', descKey: 'mega.lipsyncDesc', href: '/tools/lipsync' },
    { icon: AudioLines, labelKey: 'faceless.title', descKey: 'faceless.sub', href: '/studio/faceless' },
    { icon: Bot, labelKey: 'nav.supercomputer', descKey: 'mega.superDesc', href: '/supercomputer' },
  ],
};

function ModelRow({ model, href, onNavigate }: { model: PublicModel; href: string; onNavigate: () => void }) {
  const degraded = model.lifecycle.status === 'degraded' || model.healthScore < 0.6;
  return (
    <Link
      to={`${href}?model=${model.id}`}
      onClick={onNavigate}
      className="flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-surface-2"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-2 text-text">
        <VendorMark vendor={model.vendor} className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold">{model.displayName}</span>
          {model.ui.badge && <Badge tone={model.ui.badge === 'TOP' ? 'accent' : 'promo'}>{model.ui.badge}</Badge>}
          <span className={`size-1.5 shrink-0 rounded-full ${degraded ? 'bg-warn' : 'bg-positive'}`} aria-hidden />
        </span>
        <span className="block truncate text-xs text-muted">{model.tagline || model.vendor}</span>
      </span>
    </Link>
  );
}

export function MegaPanel({ modality, href, onNavigate }: { modality: MenuModality; href: string; onNavigate: () => void }) {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: ['models'], queryFn: () => api.models(), staleTime: 300_000 });
  const models = (data?.models ?? [])
    .filter((m) => m.modality === modality && !isToolModel(m))
    .sort((a, b) => a.ui.order - b.ui.order);

  return (
    <div
      className="w-[660px] max-w-[calc(100vw-2rem)] rounded-2xl border border-border bg-surface p-4 shadow-2xl"
      style={{ animation: 'var(--animate-slide-down)' }}
      role="menu"
    >
      <div className="grid grid-cols-[1fr_1.1fr] gap-5">
        {/* ── Features ── */}
        <div>
          <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-widest text-muted">{t('nav.features')}</p>
          <div className="space-y-0.5">
            {FEATURES[modality].map((f) => (
              <Link
                key={f.href + f.labelKey}
                to={f.href}
                onClick={onNavigate}
                className={`flex items-start gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-surface-2 ${f.soon ? 'opacity-60' : ''}`}
              >
                <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface-2 text-text">
                  <f.icon className="size-4" aria-hidden />
                </span>
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-sm font-semibold">
                    {t(f.labelKey)}
                    {f.soon && <Badge tone="muted">{t('nav.comingSoon')}</Badge>}
                  </span>
                  <span className="line-clamp-1 text-xs text-muted">{t(f.descKey)}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* ── Models (registry-fed) ── */}
        <div className="min-w-0 border-s border-border ps-5">
          <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-widest text-muted">{t('nav.models')}</p>
          <div className="max-h-[420px] space-y-0.5 overflow-y-auto rail">
            {models.map((m) => (
              <ModelRow key={m.id} model={m} href={href} onNavigate={onNavigate} />
            ))}
          </div>
          <Link
            to={`${href}?picker=1`}
            onClick={onNavigate}
            className="mt-2 flex items-center gap-1.5 rounded-xl px-2 py-2 text-xs font-semibold text-accent transition-colors hover:bg-surface-2"
          >
            {t('mega.viewAll')}
            <ArrowRight className="size-3 icon-mirror" aria-hidden />
          </Link>
        </div>
      </div>
    </div>
  );
}
